import React, { useState, useEffect } from 'react';
import { homeCareAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Home,
  MapPin,
  Clock,
  Phone,
  User,
  Calendar,
  CheckCircle,
  Play,
  Flag,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const HomeCareRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, request: null });
  const [confirmData, setConfirmData] = useState({ date: '', time: '' });

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await homeCareAPI.getAll();
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error loading requests:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId) => {
    setActionLoading(requestId);
    try {
      await homeCareAPI.accept(requestId);
      toast.success('Mission acceptée ! Contactez le patient pour confirmer le rendez-vous.');
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'acceptation');
    } finally {
      setActionLoading(null);
    }
  };

  const openConfirmModal = (request) => {
    setConfirmData({ 
      date: request.preferred_date || '', 
      time: request.preferred_time || '' 
    });
    setConfirmModal({ open: true, request });
  };

  const handleConfirm = async () => {
    if (!confirmData.date || !confirmData.time) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }
    
    setActionLoading(confirmModal.request.id);
    try {
      await homeCareAPI.confirm(confirmModal.request.id, confirmData.date, confirmData.time);
      toast.success('Rendez-vous confirmé ! Le patient a été notifié.');
      setConfirmModal({ open: false, request: null });
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la confirmation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStart = async (requestId) => {
    setActionLoading(requestId);
    try {
      await homeCareAPI.start(requestId);
      toast.success('Mission démarrée !');
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (requestId) => {
    setActionLoading(requestId);
    try {
      await homeCareAPI.complete(requestId);
      toast.success('Mission complétée ! Le patient peut maintenant vous évaluer.');
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
      accepted: { label: 'Acceptée', color: 'bg-blue-100 text-blue-700' },
      confirmed: { label: 'Confirmée', color: 'bg-purple-100 text-purple-700' },
      in_progress: { label: 'En cours', color: 'bg-cyan-100 text-cyan-700' },
      completed: { label: 'Terminée', color: 'bg-emerald-100 text-emerald-700' },
      cancelled: { label: 'Annulée', color: 'bg-slate-100 text-slate-700' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getCareTypeLabel = (type) => {
    const types = {
      nursing: 'Soins infirmiers',
      physiotherapy: 'Kinésithérapie',
      elderly_care: 'Soins aux personnes âgées',
      pediatric: 'Soins pédiatriques',
      post_op: 'Soins post-opératoires',
      other: 'Autre'
    };
    return types[type] || type;
  };

  const renderActionButtons = (request) => {
    const isLoading = actionLoading === request.id;
    
    switch (request.status) {
      case 'pending':
        return (
          <Button
            onClick={() => handleAccept(request.id)}
            disabled={isLoading}
            className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-lg"
            data-testid={`accept-btn-${request.id}`}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accepter la mission'}
          </Button>
        );
      case 'accepted':
        return (
          <Button
            onClick={() => openConfirmModal(request)}
            disabled={isLoading}
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
            data-testid={`confirm-btn-${request.id}`}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer le RDV'}
          </Button>
        );
      case 'confirmed':
        return (
          <Button
            onClick={() => handleStart(request.id)}
            disabled={isLoading}
            className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg gap-2"
            data-testid={`start-btn-${request.id}`}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                <Play className="w-4 h-4" />
                Démarrer
              </>
            )}
          </Button>
        );
      case 'in_progress':
        return (
          <Button
            onClick={() => handleComplete(request.id)}
            disabled={isLoading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg gap-2"
            data-testid={`complete-btn-${request.id}`}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                <Flag className="w-4 h-4" />
                Terminer
              </>
            )}
          </Button>
        );
      case 'completed':
        return (
          <span className="flex items-center gap-2 text-emerald-600">
            <CheckCircle className="w-5 h-5" />
            Terminée
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const myRequests = requests.filter(r => r.status !== 'pending');

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 
          className="text-2xl font-bold text-[#1A237E]"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Soins à domicile
        </h1>
        <p className="text-slate-600 mt-1">Gérez vos missions de soins à domicile</p>
      </div>

      {/* Pending Requests - Available Missions */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Missions disponibles ({pendingRequests.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingRequests.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Home className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Aucune mission disponible pour le moment</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <Card key={request.id} className="border border-amber-200 bg-amber-50/50">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900">
                              {getCareTypeLabel(request.care_type)}
                            </h3>
                            <p className="text-sm text-slate-600 mt-1">{request.description}</p>
                          </div>
                          {getStatusBadge(request.status)}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {request.patient_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {request.address}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {request.phone}
                          </span>
                          {request.preferred_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {request.preferred_date} {request.preferred_time && `à ${request.preferred_time}`}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex-shrink-0">
                        {renderActionButtons(request)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Missions */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Home className="w-5 h-5 text-[#1A237E]" />
            Mes missions ({myRequests.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {myRequests.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>Vous n'avez pas encore de missions assignées</p>
            </div>
          ) : (
            <div className="space-y-4">
              {myRequests.map((request) => (
                <Card key={request.id} className="border">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900">
                              {getCareTypeLabel(request.care_type)}
                            </h3>
                            <p className="text-sm text-slate-600 mt-1">{request.description}</p>
                          </div>
                          {getStatusBadge(request.status)}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {request.patient_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {request.address}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {request.phone}
                          </span>
                        </div>
                        
                        {request.confirmed_date && (
                          <div className="flex items-center gap-2 text-sm font-medium text-purple-700 bg-purple-50 px-3 py-2 rounded-lg w-fit">
                            <Calendar className="w-4 h-4" />
                            RDV: {request.confirmed_date} à {request.confirmed_time}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-shrink-0">
                        {renderActionButtons(request)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Modal */}
      <Dialog open={confirmModal.open} onOpenChange={(open) => !open && setConfirmModal({ open: false, request: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmer le rendez-vous</DialogTitle>
            <DialogDescription>
              Définissez la date et l'heure du rendez-vous. Le patient sera notifié par email.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Date du rendez-vous</Label>
              <Input
                type="date"
                value={confirmData.date}
                onChange={(e) => setConfirmData({ ...confirmData, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Heure du rendez-vous</Label>
              <Input
                type="time"
                value={confirmData.time}
                onChange={(e) => setConfirmData({ ...confirmData, time: e.target.value })}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setConfirmModal({ open: false, request: null })}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={actionLoading}
                className="flex-1 bg-[#E53935] hover:bg-[#D32F2F]"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomeCareRequests;
